import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { api } from '../config';
import { getAuthToken } from '../auth/tokenvalidation';
import { isRouteActive } from '../utils/routeMatch';

const ACTION_MAP = {
  create: 'canCreate',
  read: 'canRead',
  update: 'canUpdate',
  delete: 'canDelete',
  canCreate: 'canCreate',
  canRead: 'canRead',
  canUpdate: 'canUpdate',
  canDelete: 'canDelete',
};

export const flattenMenu = (items, acc = []) => {
  if (!items || !Array.isArray(items)) return acc;

  items.forEach(item => {
    if (item.link && item.link !== '/' && item.link !== '/#') {
      acc.push({
        id: item.id,
        code: item.code,
        label: item.label,
        link: item.link,
        icon: item.icon,
        isHeader: !!item.isHeader,
        orderNumber: item.orderNumber || 0,
        canCreate: !!item.canCreate,
        canRead: !!item.canRead,
        canUpdate: !!item.canUpdate,
        canDelete: !!item.canDelete,
      });
    }
    if (item.children?.length) {
      flattenMenu(item.children, acc);
    }
  });

  return acc;
};

const PermissionContext = createContext({
  menuTree: [],
  flatPermissions: [],
  isMenuLoading: true,
  getPermissionByPath: () => null,
  can: () => false,
});

export const PermissionProvider = ({ children }) => {
  const [menuTree, setMenuTree] = useState([]);
  const [isMenuLoading, setIsMenuLoading] = useState(true);
  const reduxToken = useSelector(state => state?.Login?.user?.token);
  const token = getAuthToken() || reduxToken;

  useEffect(() => {
    let cancelled = false;

    const fetchMenu = async () => {
      if (!token) {
        if (!cancelled) {
          setMenuTree([]);
          setIsMenuLoading(false);
        }
        return;
      }

      setIsMenuLoading(true);
      try {
        const response = await fetch(`${api.API_URL}/menu/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const result = await response.json();
        if (!cancelled && result.success && result.data) {
          setMenuTree(result.data);
        } else if (!cancelled) {
          setMenuTree([]);
        }
      } catch (error) {
        console.error('Menu fetch error:', error);
        if (!cancelled) setMenuTree([]);
      } finally {
        if (!cancelled) setIsMenuLoading(false);
      }
    };

    fetchMenu();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const flatPermissions = useMemo(() => flattenMenu(menuTree), [menuTree]);

  const getPermissionByPath = useCallback(
    path => {
      if (!path) return null;
      return (
        flatPermissions.find(item => isRouteActive(item.link, path)) || null
      );
    },
    [flatPermissions]
  );

  const can = useCallback(
    (action, path = window.location.pathname) => {
      const permission = getPermissionByPath(path);
      if (!permission) return false;
      const flag = ACTION_MAP[action] || action;
      return !!permission[flag];
    },
    [getPermissionByPath]
  );

  const value = useMemo(
    () => ({
      menuTree,
      flatPermissions,
      isMenuLoading,
      getPermissionByPath,
      can,
    }),
    [menuTree, flatPermissions, isMenuLoading, getPermissionByPath, can]
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermission = () => useContext(PermissionContext);

export default PermissionContext;
